import type { ReadableSpan, SpanExporter } from '@opentelemetry/sdk-trace-base'

// ── Browser RUM span-name bounding ──────────────────────────────────────────
//
// Why this exists: the OTel gateway's spanmetrics connector keys one metric
// series per distinct (service, span.name, kind, status) and has no
// `dimensions` list — so span NAMES are the only cardinality lever, and every
// distinct name lives in gateway memory until the pod dies. In prod,
// FlowhubMaui produced 34,056 of its 34,285 series from one shape:
//
//   Navigation: /customers/edit/<uuid>#currentPage=1&icat=<uuid>...
//
// which @opentelemetry/instrumentation-user-interaction writes onto the active
// click span on every history change (`span.updateName('Navigation: ' +
// pathname + hash + search)`). Each distinct path + hash became a permanent
// series and the gateway OOMKilled every ~16 days (INFRA-452).
//
// Bounding happens here, at the exporter, because that is the only place an
// already-ended span's name can be rewritten: `updateName` runs after the span
// starts, and the SDK freezes name and attributes once a span ends, so neither
// an onStart nor an onEnd SpanProcessor can do it. Only the NAME is bounded.
// Attributes (page URL, request URL, GraphQL operation) are left intact — the
// gateway never turns span attributes into series, and Tempo needs them to be
// a usable index (INFRA-476). Mirrors ecomm-and-kiosk `lib/utils/otelBounding`.

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi
const OBJECT_ID = /^[0-9a-f]{24}$/i // Mongo ObjectId
const NUMERIC = /^\d+$/

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/**
 * Collapse entity-identifying path segments to a placeholder so a path reads
 * as its route template: whole numeric / ObjectId segments → `{id}`, a UUID
 * anywhere in a segment → `{uuid}` (`cumulativeGroupId-<uuid>` included).
 * Segments are percent-decoded first so `window.location.pathname` (encoded)
 * and react-router's `location.pathname` (decoded) yield the same template.
 * Idempotent: `{uuid}` and `{id}` pass through unchanged.
 */
export const parameterizePath = (pathname: string): string =>
  pathname
    .split('/')
    .map((raw) => {
      const segment = decodeSegment(raw)
      if (NUMERIC.test(segment) || OBJECT_ID.test(segment)) return '{id}'
      return segment.replace(UUID, '{uuid}')
    })
    .join('/')

// Base for resolving a relative target. `window.location.origin` is 'null' on
// opaque origins and absent under some test harnesses; a fixed fallback keeps a
// relative path resolvable (the origin is dropped from the result anyway).
const currentOrigin = (): string => {
  const origin =
    typeof window === 'undefined' ? undefined : window.location?.origin
  return origin && origin !== 'null' ? origin : 'http://localhost'
}

// Drop the query and fragment (the app keeps filter state — with ids — in the
// hash), then parameterize the path. A relative target stays relative; an
// absolute URL keeps its origin. Unparseable input yields '/' rather than
// leaking the raw value into a name.
const boundPath = (target: string): string => {
  try {
    const url = new URL(target, currentOrigin())
    const path = parameterizePath(url.pathname)
    return /^https?:\/\//i.test(target) ? `${url.origin}${path}` : path
  } catch {
    return '/'
  }
}

// "<label> <target>" — `Navigation: /path#hash`, `route /path`. The label is a
// single token; the target is everything after the first run of whitespace so
// a decoded path with spaces (`/analytics/list/dashboards/End of Day`) is kept
// whole rather than truncated.
const LABELLED = /^([^\s/]+)\s+(.+)$/

// Names with nothing URL-shaped in them (`HTTP POST`, `click`, `documentLoad`,
// `CORS Preflight`) pass through untouched. A labelled name keeps its label and
// gets a bounded target; a bare URL or path is bounded as-is. Either way a name
// can only ever be one of (route templates × labels) — a fixed set.
const boundSpanName = (name: string): string => {
  if (!/[/#?]/.test(name)) return name
  const labelled = LABELLED.exec(name)
  return labelled ? `${labelled[1]} ${boundPath(labelled[2])}` : boundPath(name)
}

const boundSpan = (span: ReadableSpan): ReadableSpan => {
  const name = boundSpanName(span.name)
  if (name === span.name) return span
  // Shadow only `name`; spanContext(), timings, attributes, resource and scope
  // all delegate to the real span through the prototype. The original span is
  // never mutated, so other processors see it untouched.
  return Object.create(span, {
    name: { value: name, enumerable: true },
  }) as ReadableSpan
}

/**
 * Wrap a SpanExporter so every span's name is bounded immediately before it
 * leaves the browser.
 */
export const boundingExporter = (inner: SpanExporter): SpanExporter => ({
  export: (spans, resultCallback) =>
    inner.export(spans.map(boundSpan), resultCallback),
  shutdown: () => inner.shutdown(),
  forceFlush: () => inner.forceFlush?.() ?? Promise.resolve(),
})

// ── GraphQL operation attributes ────────────────────────────────────────────
//
// Apollo's HttpLink posts every operation to `<service>/query` as a JSON string
// body, so the fetch instrumentation names them all `HTTP POST`. The operation
// name is what makes a trace findable ("show me slow Checkout mutations") and
// it is schema-bounded, so it belongs on the span as an ATTRIBUTE — never in
// the name, which is what feeds spanmetrics. Keys follow the OTel GraphQL
// semantic conventions (`graphql.operation.name` / `graphql.operation.type`).

// GraphQL Name: /[_A-Za-z][_0-9A-Za-z]*/ — anchored so a value can never be
// anything but a schema identifier.
const OPERATION_NAME = /"operationName"\s*:\s*"([_A-Za-z][_0-9A-Za-z]{0,199})"/
// The query text is JSON-escaped, so leading newlines arrive as the two
// characters `\n` — skip real and escaped whitespace before the keyword.
const OPERATION_TYPE =
  /"query"\s*:\s*"(?:\s|\\[nrt])*(query|mutation|subscription)\b/

type GraphqlOperation = {
  name: string
  type?: 'query' | 'mutation' | 'subscription'
}

/**
 * Read the GraphQL operation off a fetch request, or undefined when the body is
 * not a plain JSON-object string (a `Request` with a stream body, multipart
 * uploads, batched arrays). A regex rather than `JSON.parse` — this runs on the
 * hot path of every fetch and only needs two fields.
 */
export const graphqlOperation = (
  request: Request | RequestInit | undefined
): GraphqlOperation | undefined => {
  const body = request && 'body' in request ? request.body : undefined
  if (typeof body !== 'string' || body.charCodeAt(0) !== 0x7b /* { */) {
    return undefined
  }
  const name = OPERATION_NAME.exec(body)?.[1]
  if (!name) return undefined
  const type = OPERATION_TYPE.exec(body)?.[1] as GraphqlOperation['type']
  return type ? { name, type } : { name }
}
